import React, { useState, useEffect } from 'react';
import { Trophy, Award, BarChart3, ChevronLeft, ChevronRight, Eye, X } from 'lucide-react';
import axios, { API_URL } from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PlayerAnalytics from './PlayerAnalytics';

// ========================================
// HELPER COMPONENTS
// ========================================

function TabButton({ active, onClick, icon: Icon, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center justify-center px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition ${
        active
          ? 'border-b-2 border-blue-600 text-blue-900'
          : 'text-gray-600 hover:text-blue-900'
      }`}
    >
      <Icon className="w-4 h-4 sm:mr-1" />
      <span className="hidden sm:inline ml-1">{children}</span>
    </button>
  );
}

// ========================================
// COMPOSANT DE NAVIGATION SEMAINE
// ========================================

function WeekNavigator({ currentWeek, weeks, onPrevious, onNext }) {
  const currentIndex = weeks.findIndex(w => w.id === currentWeek);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < weeks.length - 1;
  const weekData = weeks.find(w => w.id === currentWeek);

  return (
    <div className="flex items-center justify-center space-x-2 sm:space-x-4 mb-4 py-3 sm:py-4 rounded-xl border border-gray-200 bg-white">
      <button
        onClick={onPrevious}
        disabled={!hasPrevious}
        className={`p-2 rounded-lg transition ${
          hasPrevious
            ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-orange-500/30'
            : 'bg-gray-800 text-gray-600 cursor-not-allowed'
        }`}
      >
        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
      <div className="text-center min-w-[120px] sm:min-w-[200px]">
        <p className="text-base sm:text-xl font-bold text-gray-900">Semaine {weekData?.week_number}</p>
      </div>
      <button
        onClick={onNext}
        disabled={!hasNext}
        className={`p-2 rounded-lg transition ${
          hasNext
            ? 'bg-orange-600 text-white hover:bg-orange-700 shadow-lg hover:shadow-orange-500/30'
            : 'bg-gray-800 text-gray-600 cursor-not-allowed'
        }`}
      >
        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
      </button>
    </div>
  );
}

// ========================================
// HELPER FUNCTIONS
// ========================================

// Fonction pour obtenir l'emoji selon le nombre de TDs marqués
const getTouchdownEmoji = (touchdowns) => {
  if (touchdowns === 0) return '😢';
  if (touchdowns === 1) return '🙂';
  if (touchdowns === 2) return '😊';
  if (touchdowns === 3) return '😄';
  if (touchdowns === 4) return '🤩';
  return '🏆'; // 5+ TDs
};

// Format player name as "LASTNAME Suffix Firstname"
const formatPlayerName = (fullName) => {
  if (!fullName) return '';

  const parts = fullName.trim().split(' ');
  if (parts.length === 1) return fullName.toUpperCase();

  // Common suffixes to recognize (with and without periods)
  const suffixes = ['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'v'];

  // Check if last part is a suffix
  const lastPart = parts[parts.length - 1].toLowerCase().replace('.', '');
  const hasSuffix = suffixes.includes(lastPart);

  let lastName, suffix, firstName;

  if (hasSuffix) {
    // Format: Firstname Lastname Suffix → LASTNAME Suffix Firstname
    suffix = parts[parts.length - 1]; // Keep original case/punctuation
    lastName = parts[parts.length - 2].toUpperCase();
    firstName = parts.slice(0, -2).join(' ');
    return `${lastName} ${suffix} ${firstName}`;
  } else {
    // Format: Firstname Lastname → LASTNAME Firstname
    lastName = parts[parts.length - 1].toUpperCase();
    firstName = parts.slice(0, -1).join(' ');
    return `${lastName} ${firstName}`;
  }
};

// ========================================
// PLAYER DASHBOARD
// ========================================

function PlayerDashboard() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('predictions');
  const [matches, setMatches] = useState([]);
  const [userPredictions, setUserPredictions] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (currentWeek) {
      loadWeekData();
    }
  }, [currentWeek]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [weeksRes, rankingsRes] = await Promise.all([
        axios.get(`${API_URL}/weeks`),
        axios.get(`${API_URL}/rankings`)
      ]);
      setWeeks(weeksRes.data);
      setRankings(rankingsRes.data);

      // Charger la semaine courante (détection automatique)
      try {
        const currentWeekRes = await axios.get(`${API_URL}/weeks/current`);
        setCurrentWeek(currentWeekRes.data.id);
      } catch (error) {
        console.error('Erreur chargement semaine courante:', error);
        // Fallback : prendre la première semaine disponible
        if (weeksRes.data.length > 0) {
          setCurrentWeek(weeksRes.data[0].id);
        }
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeekData = async () => {
    try {
      const [matchesRes, predictionsRes] = await Promise.all([
        axios.get(`${API_URL}/matches?weekId=${currentWeek}`),
        axios.get(`${API_URL}/predictions/${currentUser.id}?weekId=${currentWeek}`)
      ]);
      setMatches(matchesRes.data);
      setUserPredictions(predictionsRes.data);
    } catch (error) {
      console.error('Erreur chargement données semaine:', error);
    }
  };

  const handlePreviousWeek = () => {
    const currentIndex = weeks.findIndex(w => w.id === currentWeek);
    // Inverser : précédent = index + 1 (car semaines triées DESC du backend)
    if (currentIndex < weeks.length - 1) {
      setCurrentWeek(weeks[currentIndex + 1].id);
    }
  };

  const handleNextWeek = () => {
    const currentIndex = weeks.findIndex(w => w.id === currentWeek);
    // Inverser : suivant = index - 1 (car semaines triées DESC du backend)
    if (currentIndex > 0) {
      setCurrentWeek(weeks[currentIndex - 1].id);
    }
  };

  const userRank = rankings.findIndex(r => r.id === currentUser.id) + 1;
  const userPoints = rankings.find(r => r.id === currentUser.id)?.points || 0;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Dashboard Joueur</h2>
            <p className="text-xs sm:text-sm text-gray-700">Bienvenue {currentUser.username}</p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-blue-900">{userPoints} pts</p>
            <p className="text-xs text-gray-600">Classement: #{userRank}</p>
          </div>
        </div>
        <div className="flex justify-around sm:justify-start sm:space-x-1 mt-4 border-b border-gray-200 overflow-x-auto">
          <TabButton active={activeTab === 'predictions'} onClick={() => setActiveTab('predictions')} icon={Trophy}>
            Mes Pronostics
          </TabButton>
          <TabButton active={activeTab === 'resultats'} onClick={() => setActiveTab('resultats')} icon={Award}>
            Mes Résultats
          </TabButton>
          <TabButton active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')} icon={BarChart3}>
            Analytics
          </TabButton>
          <TabButton active={activeTab === 'statistiques'} onClick={() => setActiveTab('statistiques')} icon={Eye}>
            Statistiques
          </TabButton>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-700 mt-3 text-sm">Chargement...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md p-4">
          {activeTab === 'predictions' && (
            <>
              <WeekNavigator
                currentWeek={currentWeek}
                weeks={weeks}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
              />
              <PlayerPredictions
                matches={matches}
                userPredictions={userPredictions}
                currentUser={currentUser}
                loadData={loadWeekData}
              />
            </>
          )}
          {activeTab === 'resultats' && currentWeek && (
            <>
              <WeekNavigator
                currentWeek={currentWeek}
                weeks={weeks}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
              />
              <PlayerResults
                weekData={matches}
                userPredictions={userPredictions}
                currentWeek={currentWeek}
                weeks={weeks}
                currentUser={currentUser}
              />
            </>
          )}
          {activeTab === 'analytics' && (
            <PlayerAnalytics userId={currentUser.id} />
          )}
          {activeTab === 'statistiques' && currentWeek && (
            <>
              <WeekNavigator
                currentWeek={currentWeek}
                weeks={weeks}
                onPrevious={handlePreviousWeek}
                onNext={handleNextWeek}
              />
              <PlayerStatistics
                selectedWeek={currentWeek}
                matches={matches}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ========================================
// JOUEUR - PRONOSTICS
// ========================================

function PlayerPredictions({ matches, userPredictions, currentUser, loadData }) {
  const [selectedPredictions, setSelectedPredictions] = useState({});
  const [matchPlayers, setMatchPlayers] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasExistingPredictions, setHasExistingPredictions] = useState(false);

  useEffect(() => {
    const predictions = {};
    let hasAny = false;
    userPredictions.forEach(pred => {
      predictions[pred.match_id] = {
        home: pred.home_player_id,
        away: pred.away_player_id
      };
      hasAny = true;
    });
    setSelectedPredictions(predictions);
    setHasExistingPredictions(hasAny);
  }, [userPredictions]);

  useEffect(() => {
    // Load players for all matches
    matches.forEach(match => {
      loadMatchPlayers(match.id);
    });
  }, [matches]);

  const loadMatchPlayers = async (matchId) => {
    if (matchPlayers[matchId]) return;

    try {
      const response = await axios.get(`${API_URL}/matches/${matchId}/players`);
      setMatchPlayers(prev => ({
        ...prev,
        [matchId]: response.data
      }));
    } catch (error) {
      console.error('Erreur chargement joueurs:', error);
    }
  };

  const handlePredictionChange = (matchId, teamType, playerId) => {
    setSelectedPredictions(prev => ({
      ...prev,
      [matchId]: {
        ...prev[matchId],
        [teamType]: parseInt(playerId) || null
      }
    }));
  };

  const handleSaveAllPredictions = async () => {
    setSaving(true);
    try {
      // Prepare all predictions for submission (ONLY for non-locked matches)
      const allPredictions = [];

      matches.forEach(match => {
        // Skip locked/finished matches
        if (!canModify(match.gameDate)) {
          return;
        }

        const predictions = selectedPredictions[match.id];
        if (predictions?.home && predictions?.away) {
          allPredictions.push({
            matchId: match.id,
            predictions: [
              { teamType: 'home', playerId: predictions.home },
              { teamType: 'away', playerId: predictions.away }
            ]
          });
        }
      });

      if (allPredictions.length === 0) {
        alert('⚠️ Aucun pronostic à enregistrer (tous les matchs sont verrouillés ou aucune sélection)');
        setSaving(false);
        return;
      }

      // Submit each prediction
      for (const pred of allPredictions) {
        await axios.post(`${API_URL}/predictions`, pred);
      }

      alert(`✅ ${allPredictions.length} pronostic(s) enregistré(s) avec succès !`);
      loadData();
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.errorType === 'expired') {
        alert('⏱️ Votre session a expiré. Vous allez être redirigé vers la page de connexion.');
        localStorage.clear();
        window.location.reload();
      } else {
        alert(error.response?.data?.message || error.response?.data?.error || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setSaving(false);
    }
  };

  const canModify = (gameDate) => {
    const lockTime = new Date(gameDate);
    // Verrouillage au coup d'envoi exact (match à 19h → verrouillé à 19h00:00)
    return new Date() < lockTime;
  };

  const hasAnyOpenMatch = matches.some(m => canModify(m.gameDate));

  return (
    <div>
      {matches.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun match disponible pour cette semaine.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3 mb-4">
            {matches.map(match => {
              const playersForMatch = matchPlayers[match.id] || [];
              // Filtrer et trier par cote croissante (du plus bas au plus haut)
              const homePlayersList = playersForMatch
                .filter(p => p.teamId === match.home_team_id)
                .sort((a, b) => a.odds - b.odds);
              const awayPlayersList = playersForMatch
                .filter(p => p.teamId === match.away_team_id)
                .sort((a, b) => a.odds - b.odds);
              const isLocked = !canModify(match.gameDate);

              return (
                <div key={match.id} className="border border-gray-200 rounded-xl p-3 sm:p-4 shadow-sm hover:shadow-xl hover:shadow-blue-900/20 transition-all duration-300 sm:hover:-translate-y-1 bg-white">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3 gap-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-xs sm:text-sm">
                            {match.homeCity} {match.homeTeam}
                          </span>
                          <span className="text-gray-600 font-bold text-xs sm:text-sm">vs</span>
                          <span className="font-semibold text-gray-900 text-xs sm:text-sm">
                            {match.awayCity} {match.awayTeam}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {new Date(match.gameDate).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-semibold whitespace-nowrap self-start sm:self-auto ${
                      isLocked ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                    }`}>
                      {isLocked ? '🔒' : '✅'} <span className="hidden sm:inline">{isLocked ? 'Verrouillé' : 'Ouvert'}</span>
                    </span>
                  </div>

                  {!matchPlayers[match.id] ? (
                    <div className="text-center py-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : homePlayersList.length === 0 || awayPlayersList.length === 0 ? (
                    <p className="text-xs sm:text-sm text-gray-700 text-center py-2 bg-yellow-50 rounded">
                      ⚠️ Les joueurs n'ont pas encore été assignés à ce match.
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5 truncate">
                          Marqueur {match.homeCity} {match.homeTeam}
                        </label>
                        <select
                          value={selectedPredictions[match.id]?.home || ''}
                          onChange={(e) => handlePredictionChange(match.id, 'home', e.target.value)}
                          disabled={isLocked}
                          className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Sélectionner un joueur...</option>
                          {homePlayersList.map(player => (
                            <option key={player.id} value={player.id}>
                              {formatPlayerName(player.name)} ({player.position}) - ×{parseFloat(player.odds).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1.5 truncate">
                          Marqueur {match.awayCity} {match.awayTeam}
                        </label>
                        <select
                          value={selectedPredictions[match.id]?.away || ''}
                          onChange={(e) => handlePredictionChange(match.id, 'away', e.target.value)}
                          disabled={isLocked}
                          className="w-full px-2 sm:px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">Sélectionner un joueur...</option>
                          {awayPlayersList.map(player => (
                            <option key={player.id} value={player.id}>
                              {formatPlayerName(player.name)} ({player.position}) - ×{parseFloat(player.odds).toFixed(2)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Single save button at the bottom */}
          {hasAnyOpenMatch && (
            <div className="sticky bottom-0 bg-white pt-3 border-t border-gray-200">
              <button
                onClick={handleSaveAllPredictions}
                disabled={saving}
                className="w-full bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-950 transition font-semibold text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Enregistrement en cours...
                  </span>
                ) : hasExistingPredictions ? (
                  '✏️ Mettre à jour mes pronostics'
                ) : (
                  '💾 Enregistrer mes pronostics'
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ========================================
// STATISTIQUES - POPULARITÉ DES JOUEURS
// ========================================

// Fonction pour calculer la couleur du gradient basée sur le pourcentage
const getGradientColor = (percentage) => {
  // 0-33%: Rouge → Orange → Jaune
  // 34-67%: Jaune → Vert clair
  // 68-100%: Vert clair → Vert foncé

  if (percentage <= 33) {
    // Rouge (#D50A0A) → Orange (#FF6B35) → Jaune (#FFC107)
    const ratio = percentage / 33;
    if (ratio < 0.5) {
      // Rouge → Orange
      const t = ratio * 2;
      const r = Math.round(213 + (255 - 213) * t);
      const g = Math.round(10 + (107 - 10) * t);
      const b = Math.round(10 + (53 - 10) * t);
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      // Orange → Jaune
      const t = (ratio - 0.5) * 2;
      const r = Math.round(255 + (255 - 255) * t);
      const g = Math.round(107 + (193 - 107) * t);
      const b = Math.round(53 + (7 - 53) * t);
      return `rgb(${r}, ${g}, ${b})`;
    }
  } else if (percentage <= 67) {
    // Jaune (#FFC107) → Vert clair (#90EE90)
    const ratio = (percentage - 34) / 33;
    const r = Math.round(255 + (144 - 255) * ratio);
    const g = Math.round(193 + (238 - 193) * ratio);
    const b = Math.round(7 + (144 - 7) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Vert clair (#90EE90) → Vert foncé (#28A745)
    const ratio = (percentage - 68) / 32;
    const r = Math.round(144 + (40 - 144) * ratio);
    const g = Math.round(238 + (167 - 238) * ratio);
    const b = Math.round(144 + (69 - 144) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

function PlayerStatistics({ selectedWeek, matches }) {
  const [statistics, setStatistics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStatistics();
  }, [selectedWeek]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/predictions/all/list?weekId=${selectedWeek}`);
      const allPredictions = response.data;

      // Count how many users made predictions
      const usersWithPredictions = new Set(allPredictions.map(p => p.user_id));
      const totalUsers = usersWithPredictions.size;

      if (totalUsers === 0) {
        setStatistics([]);
        setLoading(false);
        return;
      }

      // Count player selections
      const playerCounts = {};

      allPredictions.forEach(pred => {
        // Count home player
        if (pred.home_player_id && pred.homePlayerName) {
          const key = `${pred.home_player_id}`;
          if (!playerCounts[key]) {
            playerCounts[key] = {
              playerId: pred.home_player_id,
              playerName: pred.homePlayerName,
              teamCity: pred.homeCity,
              teamName: pred.homeTeam,
              count: 0
            };
          }
          playerCounts[key].count++;
        }

        // Count away player
        if (pred.away_player_id && pred.awayPlayerName) {
          const key = `${pred.away_player_id}`;
          if (!playerCounts[key]) {
            playerCounts[key] = {
              playerId: pred.away_player_id,
              playerName: pred.awayPlayerName,
              teamCity: pred.awayCity,
              teamName: pred.awayTeam,
              count: 0
            };
          }
          playerCounts[key].count++;
        }
      });

      // Calculate percentages and sort
      const stats = Object.values(playerCounts)
        .map(player => ({
          ...player,
          percentage: Math.round((player.count / totalUsers) * 100)
        }))
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, 20); // Top 20 players

      setStatistics(stats);
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900">📊 Popularité des Joueurs</h3>
        <p className="text-sm text-gray-700 mt-1">
          Les joueurs les plus sélectionnés par nos pronostiqueurs cette semaine
        </p>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : statistics.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg">
          <p className="text-gray-700 text-sm">Aucun pronostic disponible pour cette semaine.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {statistics.map((player, index) => (
            <div key={player.playerId} className="border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-xl hover:shadow-blue-900/20 transition-all duration-300 hover:-translate-y-1 bg-white">
              <div className="flex items-center gap-3 mb-2">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                  index === 0 ? 'bg-yellow-100 text-yellow-700' :
                  index === 1 ? 'bg-gray-700 text-gray-200' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-50 text-blue-900'
                }`}>
                  {index + 1}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm">
                    {player.playerName}
                  </p>
                  <p className="text-xs text-gray-600">
                    {player.teamCity} {player.teamName}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-blue-900">{player.percentage}%</p>
                  <p className="text-xs text-gray-600">{player.count} choix</p>
                </div>
              </div>

              {/* Horizontal progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="h-2.5 rounded-full transition-all"
                  style={{
                    width: `${player.percentage}%`,
                    backgroundColor: getGradientColor(player.percentage)
                  }}
                ></div>
              </div>

              <p className="text-xs text-gray-600 mt-1.5 text-center">
                Sélectionné par {player.percentage}% de nos pronostiqueurs
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ========================================
// PLAYER RESULTS - Résultats des pronostics
// ========================================

function PlayerResults({ weekData, userPredictions, currentWeek, weeks, currentUser }) {
  // State pour stocker les joueurs de chaque match
  const [matchPlayers, setMatchPlayers] = useState({});

  // Récupérer les données de la semaine actuelle
  const selectedWeek = weeks.find(w => w.id === currentWeek);

  // Filtrer les pronostics de la semaine avec résultats disponibles
  // Afficher tous les matchs terminés, y compris ceux avec 0 points
  // Note: userPredictions est déjà filtré par semaine via l'API (?weekId=X)
  const weekPredictions = userPredictions.filter(pred => {
    const match = weekData.find(m => m.id == pred.match_id); // Using == for type coercion

    if (!match) {
      return false;
    }

    // Check for both 'finished' and 'completed' status
    const isFinished = match.status === 'finished' || match.status === 'completed';
    return isFinished;
  });

  // Charger les joueurs pour tous les matchs terminés
  useEffect(() => {
    const loadAllMatchPlayers = async () => {
      for (const prediction of weekPredictions) {
        const matchId = prediction.match_id;

        // Skip if already loaded
        if (matchPlayers[matchId]) continue;

        try {
          const response = await axios.get(`${API_URL}/matches/${matchId}/players`);
          setMatchPlayers(prev => ({
            ...prev,
            [matchId]: response.data
          }));
        } catch (error) {
          console.error('Erreur chargement joueurs du match:', error);
        }
      }
    };

    if (weekPredictions.length > 0) {
      loadAllMatchPlayers();
    }
  }, [weekPredictions.length, currentWeek]); // Reload when week changes

  // Récupérer tous les pronostics depuis la semaine 1 pour le bilan cumulé
  // Note: userPredictions ne contient que la semaine courante, pas toutes les semaines
  const cumulativePredictions = userPredictions.filter(pred => {
    const match = weekData.find(m => m.id == pred.match_id); // Using == for type coercion
    return match && (match.status === 'finished' || match.status === 'completed');
  });
  const cumulativePoints = cumulativePredictions.reduce((sum, pred) => sum + (Number(pred.points_earned) || 0), 0);
  const cumulativeMatches = cumulativePredictions.length;

  // Calculer le total de la semaine
  const weekPoints = weekPredictions.reduce((sum, pred) => sum + (Number(pred.points_earned) || 0), 0);
  const weekMatches = weekPredictions.length;

  // Fonction pour aller vers le classement général (page d'accueil)
  const goToLeaderboard = () => {
    // Recharger la page pour retourner à l'accueil où se trouve le classement
    window.location.reload();
  };

  // Vérifier si un match est terminé
  const isMatchFinished = (matchStatus) => {
    return matchStatus === 'finished' || matchStatus === 'completed';
  };

  return (
    <div className="space-y-6">

      {/* Section des résultats par match */}
      <div className="space-y-4">
        <h3 className="text-xl font-bold" style={{ color: '#013369' }}>
          Mes Résultats - Semaine {selectedWeek?.week_number || currentWeek}
        </h3>

        {weekPredictions.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
            Aucun résultat disponible pour cette semaine.
          </div>
        ) : (
          weekPredictions.map((prediction) => {
            const match = weekData.find(m => m.id == prediction.match_id); // Using == for type coercion
            if (!match) {
              return null;
            }

            const finished = isMatchFinished(match.status);

            // Récupérer les joueurs du match depuis le state matchPlayers
            const playersForMatch = matchPlayers[match.id] || [];
            const homePlayer = playersForMatch.find(p => p.id === prediction.home_player_id);
            const awayPlayer = playersForMatch.find(p => p.id === prediction.away_player_id);

            // Extraire l'heure du gameDate (format ISO: 2025-10-31T02:00:00)
            const matchTime = new Date(match.gameDate).toLocaleTimeString('fr-FR', {
              hour: '2-digit',
              minute: '2-digit'
            });

            return (
              <div key={prediction.id} className="bg-white rounded-lg shadow-md p-6">
                {/* En-tête du match */}
                <div className="flex justify-between items-center mb-4 pb-4 border-b">
                  <div>
                    <h4 className="font-bold text-lg" style={{ color: '#013369' }}>
                      {match.homeCity} {match.homeTeam} vs {match.awayCity} {match.awayTeam}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {new Date(match.gameDate).toLocaleDateString('fr-FR')} - {matchTime}
                    </p>
                  </div>
                  <div className="text-right">
                    <span
                      className="inline-block px-3 py-1 rounded-full text-sm font-bold text-white"
                      style={{ backgroundColor: finished ? '#28a745' : '#6c757d' }}
                    >
                      {finished ? 'Terminé' : 'En cours'}
                    </span>
                  </div>
                </div>

                {/* Résultats des pronostics */}
                {!finished ? (
                  <div className="text-center py-4 text-gray-500">
                    Résultats non disponibles
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Joueur équipe domicile */}
                    {homePlayer && (
                      <div className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: '#F5F7FA' }}>
                        <div className="flex-1">
                          <span className="font-semibold" style={{ color: '#013369' }}>
                            {homePlayer.name} ({match.homeCity} {match.homeTeam})
                          </span>
                          <span className="text-sm text-gray-600 ml-2">
                            - Cote {homePlayer.odds}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {getTouchdownEmoji(homePlayer.touchdowns_scored || 0)}
                          </span>
                          <span className="font-bold" style={{ color: '#D50A0A' }}>
                            {homePlayer.touchdowns_scored || 0} TD{(homePlayer.touchdowns_scored || 0) > 1 ? 's' : ''}
                          </span>
                          <span className="font-bold text-lg" style={{ color: '#013369' }}>
                            {Math.round((homePlayer.touchdowns_scored || 0) * homePlayer.odds)} pts
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Joueur équipe extérieure */}
                    {awayPlayer && (
                      <div className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: '#F5F7FA' }}>
                        <div className="flex-1">
                          <span className="font-semibold" style={{ color: '#013369' }}>
                            {awayPlayer.name} ({match.awayCity} {match.awayTeam})
                          </span>
                          <span className="text-sm text-gray-600 ml-2">
                            - Cote {awayPlayer.odds}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">
                            {getTouchdownEmoji(awayPlayer.touchdowns_scored || 0)}
                          </span>
                          <span className="font-bold" style={{ color: '#D50A0A' }}>
                            {awayPlayer.touchdowns_scored || 0} TD{(awayPlayer.touchdowns_scored || 0) > 1 ? 's' : ''}
                          </span>
                          <span className="font-bold text-lg" style={{ color: '#013369' }}>
                            {Math.round((awayPlayer.touchdowns_scored || 0) * awayPlayer.odds)} pts
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Total du match */}
                    <div className="pt-3 border-t mt-3">
                      <div className="flex justify-end">
                        <span className="text-lg font-bold" style={{ color: '#013369' }}>
                          Total du match: {prediction.points_earned || 0} points
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Résumé de la semaine et bilan cumulé */}
      {weekPredictions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Résumé de la semaine */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h4 className="font-bold text-lg mb-4" style={{ color: '#013369' }}>
              Résumé Semaine {selectedWeek?.week_number || currentWeek}
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Matchs joués:</span>
                <span className="font-bold">{weekMatches}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Points gagnés:</span>
                <span className="font-bold text-xl" style={{ color: '#D50A0A' }}>
                  {Math.round(weekPoints)} pts
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Moyenne par match:</span>
                <span className="font-bold">
                  {weekMatches > 0 ? (weekPoints / weekMatches).toFixed(1) : '0.0'} pts
                </span>
              </div>
            </div>
          </div>

          {/* Bilan cumulé */}
          <div className="bg-white rounded-lg shadow-md p-6" style={{ borderLeft: '4px solid #D50A0A' }}>
            <h4 className="font-bold text-lg mb-4" style={{ color: '#013369' }}>
              Bilan Cumulé (Saison {selectedWeek?.season || new Date().getFullYear()})
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Total matchs:</span>
                <span className="font-bold">{cumulativeMatches}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total points:</span>
                <span className="font-bold text-2xl" style={{ color: '#D50A0A' }}>
                  {Math.round(cumulativePoints)} pts
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Moyenne par match:</span>
                <span className="font-bold">
                  {cumulativeMatches > 0 ? (cumulativePoints / cumulativeMatches).toFixed(1) : '0.0'} pts
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bouton vers le classement */}
      {weekPredictions.length > 0 && (
        <div className="flex justify-center mt-6">
          <button
            onClick={goToLeaderboard}
            className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-bold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#013369' }}
          >
            <Award size={20} />
            Voir le classement général
          </button>
        </div>
      )}
    </div>
  );
}

// ========================================
// MODAL
// ========================================

function Modal({ onClose, title, children, large }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg shadow-xl ${large ? 'max-w-4xl' : 'max-w-md'} w-full max-h-[90vh] overflow-y-auto`}>
        <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

export default PlayerDashboard;
